import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from '@/contexts/authContext';
import { initI18n } from '@/i18n/i18n';
import { Box, CircularProgress, Typography } from '@mui/material';
import { Toaster } from 'sonner';

// 懒加载：管理端
const AdminLayout = lazy(() => import('@/components/admin/AdminLayout'));
const AdminLogin = lazy(() => import('@/pages/admin/Login'));
const AdminDashboard = lazy(() => import('@/pages/admin/Dashboard'));
const Communities = lazy(() => import('@/pages/admin/Communities'));
const UserManagement = lazy(() => import('@/pages/admin/UserManagement'));
const Merchants = lazy(() => import('@/pages/admin/Merchants'));
const Products = lazy(() => import('@/pages/admin/Products'));
const AdminOrderManagement = lazy(() => import('@/pages/admin/OrderManagement'));
const PointsDashboard = lazy(() => import('@/pages/admin/PointsDashboard'));
const PointsPricingConfig = lazy(() => import('@/pages/admin/PointsPricingConfig'));
const AdminManagement = lazy(() => import('@/pages/admin/AdminManagement'));
const RoleManagement = lazy(() => import('@/pages/admin/RoleManagement'));
const ChangePassword = lazy(() => import('@/pages/admin/ChangePassword'));
const ContentModeration = lazy(() => import('@/pages/admin/ContentModeration'));
const RiskControl = lazy(() => import('@/pages/admin/RiskControl'));
const Advertisements = lazy(() => import('@/pages/admin/Advertisements'));
const LegalDocuments = lazy(() => import('@/pages/admin/LegalDocuments'));
const CustomerService = lazy(() => import('@/pages/admin/CustomerService'));
const ElectionSettings = lazy(() => import('@/pages/admin/ElectionSettings'));
const ForgotPasswordAdmin = lazy(() => import('@/pages/admin/ForgotPassword'));
const MerchantOrders = lazy(() => import('@/pages/admin/MerchantOrders'));
const MerchantOrderRights = lazy(() => import('@/pages/admin/MerchantOrderRights'));
const DocumentManagement = lazy(() => import('@/pages/admin/DocumentManagement'));
const SubscriptionManagement = lazy(() => import('@/pages/admin/SubscriptionManagement'));
const SurnameCultureReview = lazy(() => import('@/pages/admin/SurnameCultureReview'));
// 新增页面
const CreditScoreManagement = lazy(() => import('@/pages/admin/CreditScoreManagement'));
const ReviewManagement = lazy(() => import('@/pages/admin/ReviewManagement'));
const CommunityVideoReview = lazy(() => import('@/pages/admin/CommunityVideoReview'));
const CommunityPointsManagement = lazy(() => import('@/pages/admin/CommunityPointsManagement'));
const ShareFunnelDashboard = lazy(() => import('@/pages/admin/ShareFunnelDashboard'));
const VillageProductManagement = lazy(() => import('@/pages/admin/VillageProductManagement'));
const FinanceManagement = lazy(() => import('@/pages/admin/FinanceManagement'));
const AnalyticsDashboard = lazy(() => import('@/pages/admin/AnalyticsDashboard'));

// 加载中组件
const LoadingFallback = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
    <Box textAlign="center">
      <CircularProgress />
      <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
        加载中...
      </Typography>
    </Box>
  </Box>
);

// 管理端路由守卫
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isChecking, setIsChecking] = React.useState(true);
  const [redirectPath, setRedirectPath] = React.useState<string | null>(null);

  React.useEffect(() => {
    try {
      // 同时检查 localStorage 和 sessionStorage
      const storedAuth = localStorage.getItem('auth') || sessionStorage.getItem('auth');
      const authToken = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      let isAuthenticated = false;
      let userRole = 'user';

      if (storedAuth && authToken) {
        try {
          const authData = JSON.parse(storedAuth);
          // 检查认证状态：有 token 且 authData 中有 isAuthenticated 或 role 字段
          isAuthenticated = (authData.isAuthenticated === true || authData.role) && authToken.length > 0;
          userRole = authData.role || 'user';
          
          console.log('[ProtectedRoute] 认证检查:', {
            hasAuth: !!storedAuth,
            hasToken: !!authToken,
            tokenLength: authToken?.length,
            isAuthenticated,
            userRole,
            authDataKeys: Object.keys(authData)
          });
        } catch (error) {
          console.error('[ProtectedRoute] 解析认证数据失败:', error);
          localStorage.removeItem('auth');
          localStorage.removeItem('authToken');
          sessionStorage.removeItem('auth');
          sessionStorage.removeItem('authToken');
        }
      }

      if (!isAuthenticated) {
        console.warn('[ProtectedRoute] 未认证，重定向到登录页');
        setRedirectPath('/admin/login');
      } else {
        // 允许的角色：admin, super_admin, operator, merchant_admin（管理端登录时会被映射为 admin）
        const allowedRoles = ['admin', 'super_admin', 'operator', 'merchant_admin'];
        if (!allowedRoles.includes(userRole)) {
          console.warn(`[ProtectedRoute] 角色 ${userRole} 不允许访问管理端，允许的角色:`, allowedRoles);
          setRedirectPath('/admin/login');
        } else {
          console.log(`[ProtectedRoute] 认证通过，角色: ${userRole}`);
          setRedirectPath(null);
        }
      }
    } catch (error) {
      console.error('[ProtectedRoute] 检查认证状态时出错:', error);
      setRedirectPath('/admin/login');
    } finally {
      setIsChecking(false);
    }
  }, []);

  if (isChecking) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (redirectPath) {
    return <Navigate to={redirectPath} replace />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  useEffect(() => {
    initI18n().catch(error => {
      console.error('Failed to initialize i18n:', error);
    });
  }, []);

  return (
    <AuthProvider>
      <Router>
        <div className="min-h-screen flex flex-col">
          <main className="flex-grow">
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                {/* 根路径重定向到管理端 */}
                <Route path="/" element={<Navigate to="/admin" replace />} />
                
                {/* 用户端登录路由 - 重定向到管理端登录 */}
                <Route path="/user/login" element={<Navigate to="/admin/login" replace />} />
                
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin/forgot-password" element={<ForgotPasswordAdmin />} />
                {/* 兼容旧版侧边栏路径 */}
                <Route path="/admin/admin-management" element={<Navigate to="/admin/admins" replace />} />
                <Route path="/admin/role-management" element={<Navigate to="/admin/roles" replace />} />

                {/* 管理端核心功能路由 */}
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <AdminDashboard />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/dashboard"
                  element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <AdminDashboard />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/communities"
                  element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <Communities />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/users"
                  element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <UserManagement />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/user-management"
                  element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <UserManagement />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/merchants"
                  element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <Merchants />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/products"
                  element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <Products />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/order-management"
                  element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <AdminOrderManagement />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/points"
                  element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <PointsDashboard />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/points/pricing-config"
                  element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <PointsPricingConfig />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/admins"
                  element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <AdminManagement />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/roles"
                  element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <RoleManagement />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/change-password"
                  element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <ChangePassword />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/content-moderation"
                  element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <ContentModeration />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/risk-control"
                  element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <RiskControl />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/advertisements"
                  element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <Advertisements />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/legal-documents"
                  element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <LegalDocuments />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/customer-service"
                  element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <CustomerService />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/election-settings"
                  element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <ElectionSettings />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/merchant-orders"
                  element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <MerchantOrders />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/merchant-order-rights"
                  element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <MerchantOrderRights />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/financial-report"
                  element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <FinanceManagement />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/document-management"
                  element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <DocumentManagement />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/subscription-management"
                  element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <SubscriptionManagement />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/surname-culture-review"
                  element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <SurnameCultureReview />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                {/* 新增路由 */}
                <Route
                  path="/admin/credit-score-management"
                  element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <CreditScoreManagement />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/review-management"
                  element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <ReviewManagement />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/community-video-review"
                  element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <CommunityVideoReview />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/community-points-management"
                  element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <CommunityPointsManagement />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/share-funnel"
                  element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <ShareFunnelDashboard />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                {/* 一村一品PK评选管理 */}
                <Route
                  path="/admin/village-product"
                  element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <VillageProductManagement />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                {/* 财务管理 */}
                <Route
                  path="/admin/finance"
                  element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <FinanceManagement />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                {/* 数据分析 */}
                <Route
                  path="/admin/analytics"
                  element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <AnalyticsDashboard />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </Suspense>
          </main>
        </div>
      </Router>
      <Toaster position="bottom-right" />
    </AuthProvider>
  );
};

export default App;
